import { type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { WifiOffIcon } from '@ttab/elephant-ui/icons'

/**
 * What the dialog says while the browser has no connection: a PM cannot be
 * created until it is back.
 *
 * The pill is the one Wires shows for the same thing
 * (`views/Wires/components/StreamsBanner.tsx`), but it is pinned differently.
 * Wires positions its banner absolutely inside a `relative` parent of its own;
 * the memo dialog has no such parent - the thing that actually scrolls is the
 * dialog popup above the whole view (`max-h-[92dvh] overflow-y-auto` in
 * `packages/elephant-ui/src/components/ui/dialog.tsx`), so an absolute banner
 * would scroll away with the form.
 *
 * Hence `sticky`, which anchors to that popup - but only from outside
 * `View.Content`, whose ScrollArea viewport would otherwise become the nearest
 * scrollport and never scroll. `h-0` is what keeps the pill floating over the
 * form instead of pushing it down: a sticky box takes up flow space, and a
 * zero-height one has none to take.
 */
export const OfflineBanner = (): JSX.Element => {
  const { t } = useTranslation('memo')

  return (
    <div className='sticky top-0 z-30 h-0 flex justify-center pointer-events-none'>
      <div className='mt-1 border bg-background rounded-lg text-sm px-5 py-3 shadow-xl flex items-center gap-2 pointer-events-auto'>
        <WifiOffIcon className='animate-pulse fill-red-600 stroke-red-600' size={18} strokeWidth={2.05} />
        <span>{t('banner.offline')}</span>
      </div>
    </div>
  )
}
